
import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export interface HiddenTagsState {
  [tagId: string]: boolean;
}

export const useHiddenTags = () => {
  const [hiddenTags, setHiddenTags] = useState<HiddenTagsState>({});
  const [lastChangedTagId, setLastChangedTagId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Load hidden tags from localStorage on mount
  useEffect(() => {
    const storedHiddenTags = localStorage.getItem('hiddenTags');
    if (storedHiddenTags) {
      try {
        const parsed = JSON.parse(storedHiddenTags);
        setHiddenTags(parsed);
        console.log('[TAG VISIBILITY] Loaded hidden tags from storage:', parsed);
      } catch (error) {
        console.error('Error parsing hidden tags:', error);
      }
    }
  }, []);

  // Save to localStorage whenever hiddenTags changes
  useEffect(() => {
    localStorage.setItem('hiddenTags', JSON.stringify(hiddenTags));
    
    // Invalidate queries in a more controlled manner
    console.log('[TAG VISIBILITY] Invalidating queries for tag visibility change');
    
    // Invalidate content tag relations only - this is essential
    queryClient.invalidateQueries({ queryKey: ['contentTagRelations'] });
    
    // Dispatch a custom event that other components can listen for
    const event = new CustomEvent('hiddenTagsChanged', { 
      detail: hiddenTags 
    });
    window.dispatchEvent(event);
    
    // Only do page refreshes for the specific tag page we're on
    if (lastChangedTagId) {
      const currentUrl = window.location.href;
      
      // If we're on a tag page for the specific tag that was changed,
      // use query invalidation instead of full page refresh
      if (currentUrl.includes(`/tag/${lastChangedTagId}`)) {
        console.log(`[TAG VISIBILITY] On tag page for changed tag ${lastChangedTagId}, invalidating tag content`);
        queryClient.invalidateQueries({ queryKey: ['tagContent'] });
      }
      
      // Clear the lastChangedTagId after handling the refresh
      setLastChangedTagId(null);
    }
    
    console.log('[TAG VISIBILITY] Updated hidden tags, invalidated queries:', hiddenTags);
  }, [hiddenTags, queryClient, lastChangedTagId]);

  // Use useCallback to ensure function reference stability
  const toggleTagVisibility = useCallback((tagId: string) => {
    setHiddenTags(prev => {
      const newState = { 
        ...prev, 
        [tagId]: !prev[tagId] 
      };
      
      // Log the change to help with debugging
      console.log(`[TAG VISIBILITY] Toggled visibility for tag ${tagId}:`, {
        wasHidden: prev[tagId],
        isNowHidden: !prev[tagId],
        allHiddenTags: newState
      });
      
      return newState;
    });
    
    // Set the lastChangedTagId to track which tag was toggled
    setLastChangedTagId(tagId);
  }, []);

  const isTagHidden = useCallback((tagId: string): boolean => {
    return !!hiddenTags[tagId];
  }, [hiddenTags]);

  return {
    hiddenTags,
    toggleTagVisibility,
    isTagHidden
  };
};
